import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, ExternalLink, Copy, X, AlertTriangle, Lock, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { InvoiceRowActions } from "@/components/invoices/InvoiceRowActions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Heuristic: pick the right explorer for a given tx hash string.
// EVM (0x + 64 hex) → Etherscan; 64-hex no-prefix → mempool.space (BTC);
// base58-ish 80-90 chars → Solscan. Falls back to null when unknown.
const explorerFor = (hash) => {
  if (!hash) return null;
  const h = hash.trim();
  if (/^0x[a-fA-F0-9]{64}$/.test(h)) return { name: "Etherscan", url: `https://etherscan.io/tx/${h}` };
  if (/^[a-fA-F0-9]{64}$/.test(h)) return { name: "mempool.space", url: `https://mempool.space/tx/${h}` };
  if (/^[1-9A-HJ-NP-Za-km-z]{80,90}$/.test(h)) return { name: "Solscan", url: `https://solscan.io/tx/${h}` };
  return null;
};

const shortHash = (h) => (h && h.length > 14 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h);

const StatusPill = ({ s }) => {
  const label = s === "pending_confirmation" ? "awaiting confirm" : s;
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 border whitespace-nowrap ${
        s === "paid"
          ? "border-emerald-500/40 text-emerald-400"
          : s === "overdue"
          ? "border-rose-500/40 text-rose-400"
          : s === "pending_confirmation"
          ? "border-amber-500/40 text-amber-400 bg-amber-500/5"
          : s === "sent"
          ? "border-amber-500/40 text-amber-400"
          : "border-zinc-700 text-zinc-400"
      }`}
    >
      {label}
    </span>
  );
};

const Invoices = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [followup, setFollowup] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // invoice obj being confirmed
  const [confirming, setConfirming] = useState(false);
  const [billing, setBilling] = useState(null); // {tier, limits:{max_invoices_per_month, watermark}}

  const load = useCallback(async () => {
    try {
      const r = await api.get("/invoices");
      setList(r.data);
    } catch (e) {
      console.error("Failed to load invoices:", e);
      toast.error("Could not load invoices");
    }
  }, []);
  const loadBilling = useCallback(async () => {
    try {
      const r = await api.get("/billing/status");
      setBilling(r.data);
    } catch (e) {
      // Quota UI just hides when this fails — log only so prod issues
      // are still discoverable in the console without nagging the user.
      console.warn("Billing status unavailable; quota UI will hide:", e);
    }
  }, []);
  useEffect(() => { load(); loadBilling(); }, [load, loadBilling]);

  // --- Quota math (free tier only) ---
  const tier = billing?.tier || "free";
  const monthlyCap = billing?.limits?.max_invoices_per_month ?? null; // null = unlimited
  const monthStart = (() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
  })();
  const monthCount = list.filter((i) => (i.created_at || "") >= monthStart).length;
  const quotaPct = monthlyCap ? Math.min(100, Math.round((monthCount / monthlyCap) * 100)) : 0;
  const atLimit = monthlyCap !== null && monthCount >= monthlyCap;
  const nearLimit = monthlyCap !== null && monthCount >= monthlyCap - 1 && !atLimit;

  const send = async (id) => {
    try {
      await api.post(`/invoices/${id}/send`, { origin_url: window.location.origin });
      toast.success("Invoice sent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
  };

  const remind = async (id) => {
    try {
      await api.post(`/invoices/${id}/remind`, { origin_url: window.location.origin });
      toast.success("Reminder sent");
    } catch (e) {
      toast.error("Failed to remind");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete invoice?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Deleted");
    load();
  };

  const confirmPayment = async () => {
    if (!confirmTarget) return;
    setConfirming(true);
    try {
      await api.post(`/invoices/${confirmTarget.invoice_id}/confirm-payment`);
      toast.success("Invoice marked paid");
      setConfirmTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    } finally {
      setConfirming(false);
    }
  };

  const copyTx = (hash) => {
    navigator.clipboard.writeText(hash);
    toast.success("Transaction hash copied");
  };

  const verifyOnchain = async (id, number) => {
    toast.loading("Checking the blockchain…", { id: `verify-${id}` });
    try {
      const r = await api.post(`/invoices/${id}/verify-onchain`);
      toast.dismiss(`verify-${id}`);
      if (r.data?.ok && r.data?.reason === "verified") {
        toast.success(`${number} confirmed on-chain — marked paid`);
        load();
      } else if (r.data?.reason === "already_paid") {
        toast.success("Already paid");
        load();
      } else {
        const status = r.data?.info?.status || r.data?.reason || "not confirmed yet";
        toast.message(`Not confirmed yet`, { description: `Explorer says: ${status}` });
      }
    } catch (e) {
      toast.dismiss(`verify-${id}`);
      toast.error(e?.response?.data?.detail || "Verification failed");
    }
  };

  const ai = async (id, number) => {
    setFollowup({ loading: true, number });
    try {
      const r = await api.post(`/ai/followup/${id}`);
      setFollowup({ loading: false, number, text: r.data.draft });
    } catch {
      setFollowup(null);
      toast.error("AI draft failed");
    }
  };

  const copyLink = (id) => {
    navigator.clipboard.writeText(`${window.location.origin}/pay/${id}`);
    toast.success("Payment link copied");
  };

  const downloadPdf = async (id, number) => {
    try {
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quill-${number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download PDF");
    }
  };

  const rowActions = { send, remind, verifyOnchain, confirm: setConfirmTarget, ai, copyLink, downloadPdf, del };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        overline="Billing"
        title="Invoices"
        description="Send branded invoices, get paid directly to your crypto wallet, and let AI draft follow-ups."
        actions={
          <TooltipProvider delayDuration={120}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <button
                    onClick={() => !atLimit && navigate("/invoices/new")}
                    disabled={atLimit}
                    data-testid="create-invoice-btn"
                    className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md transition-colors ${
                      atLimit
                        ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                        : "bg-white text-black hover:bg-zinc-200"
                    }`}
                  >
                    {atLimit ? <Lock size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
                    {atLimit ? "Limit reached" : "New invoice"}
                  </button>
                </span>
              </TooltipTrigger>
              {atLimit && (
                <TooltipContent
                  side="bottom"
                  data-testid="quota-tooltip"
                  className="bg-[#0d0d0d] border border-[#2c241e] text-zinc-200 sm:rounded-none px-3 py-2 text-xs max-w-[240px]"
                >
                  <div className="font-medium text-white mb-0.5">Free tier limit reached</div>
                  <div className="text-zinc-400">Upgrade to Pro for unlimited invoices.</div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        }
      />

      <div className="px-4 md:px-8 pt-2">
        {tier === "free" && monthlyCap !== null && (
          <div
            data-testid="quota-meter"
            className={`border ${atLimit ? "border-rose-500/30 bg-rose-500/5" : nearLimit ? "border-amber-500/30 bg-amber-500/[0.03]" : "border-[#2c241e] bg-[#0d0d0d]"} px-5 py-4 flex items-center gap-5`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Free tier · this month</span>
                <span
                  data-testid="quota-count"
                  className={`text-xs font-mono ${atLimit ? "text-rose-400" : "text-zinc-300"}`}
                >
                  {monthCount} / {monthlyCap}
                </span>
              </div>
              <div className="mt-2 h-[3px] bg-[#1a1a1a] overflow-hidden">
                <div
                  data-testid="quota-bar"
                  className={`h-full transition-all duration-500 ${atLimit ? "bg-rose-500/70" : nearLimit ? "bg-amber-400/80" : "bg-emerald-400/70"}`}
                  style={{ width: `${quotaPct}%` }}
                />
              </div>
              <div className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                {atLimit
                  ? "You've used your free invoices for the month. Upgrade to keep billing clients."
                  : nearLimit
                  ? "One invoice left this month on the Free plan."
                  : "Free plan includes 3 invoices per calendar month."}
              </div>
            </div>
            <Link
              to="/pricing"
              data-testid="quota-upgrade-btn"
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border transition-colors whitespace-nowrap ${
                atLimit
                  ? "border-white bg-white text-black hover:bg-zinc-200"
                  : "border-[#2c241e] text-zinc-300 hover:border-white hover:text-white"
              }`}
            >
              <Zap size={12} strokeWidth={2} /> Upgrade
            </Link>
          </div>
        )}
      </div>

      <div className="p-4 md:p-8">
        <div className={`border border-[#2c241e] ${list.length > 0 ? "hidden md:block" : ""}`}>
          {list.length === 0 ? (
            <div className="p-16 text-center" data-testid="invoices-empty">
              <div className="font-display text-2xl tracking-tight">No invoices yet</div>
              <div className="text-zinc-400 text-sm mt-2">Create one to start getting paid.</div>
              <button
                onClick={() => navigate("/invoices/new")}
                data-testid="empty-create-invoice-btn"
                className="mt-6 inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200"
              >
                <Plus size={14} strokeWidth={2.5} /> Create your first invoice
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#2c241e]">
                  <th className="p-4 font-normal">Number</th>
                  <th className="p-4 font-normal">Client</th>
                  <th className="p-4 font-normal">Due</th>
                  <th className="p-4 font-normal text-right">Total</th>
                  <th className="p-4 font-normal">Status</th>
                  <th className="p-4 font-normal text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-[#2c241e] last:border-b-0 hover:bg-[#1f1815]" data-testid={`invoice-row-${inv.number}`}>
                    <td className="p-4 font-mono text-sm">{inv.number}</td>
                    <td className="p-4 text-sm">
                      <div>{inv.client_name}</div>
                      <div className="text-xs text-zinc-500">{inv.client_email}</div>
                    </td>
                    <td className="p-4 text-sm text-zinc-400">{fmtDate(inv.due_date)}</td>
                    <td className="p-4 text-right font-mono text-sm">{fmtMoney(inv.total, inv.currency)}</td>
                    <td className="p-4">
                      <StatusPill s={inv.status} />
                      {inv.status === "pending_confirmation" && inv.payment_tx_hash && (
                        (() => {
                          const ex = explorerFor(inv.payment_tx_hash);
                          return (
                            <div
                              className="mt-1.5 flex items-center gap-1.5 text-[10px] text-zinc-500 font-mono"
                              data-testid={`tx-hash-${inv.number}`}
                            >
                              <span className="text-zinc-600">tx</span>
                              <span className="text-zinc-300">{shortHash(inv.payment_tx_hash)}</span>
                              <button
                                onClick={() => copyTx(inv.payment_tx_hash)}
                                title="Copy tx hash"
                                aria-label="Copy transaction hash"
                                data-testid={`copy-tx-${inv.number}`}
                                className="text-zinc-500 hover:text-white transition-colors"
                              ><Copy size={10} strokeWidth={2} /></button>
                              {ex && (
                                <a
                                  href={ex.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={`Open on ${ex.name}`}
                                  aria-label={`Open transaction on ${ex.name}`}
                                  data-testid={`explorer-${inv.number}`}
                                  className="text-zinc-500 hover:text-emerald-400 transition-colors"
                                ><ExternalLink size={10} strokeWidth={2} /></a>
                              )}
                            </div>
                          );
                        })()
                      )}
                      {inv.status === "pending_confirmation" && !inv.payment_tx_hash && (
                        <div className="mt-1.5 text-[10px] text-zinc-500" data-testid={`no-tx-${inv.number}`}>
                          no tx hash provided
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <InvoiceRowActions inv={inv} actions={rowActions} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Mobile card list (< md) */}
        {list.length > 0 && (
          <div className="md:hidden space-y-3" data-testid="invoices-mobile-list">
            {list.map((inv) => (
              <div
                key={inv.invoice_id}
                className="border border-[#2c241e] bg-[#1b130f]/40 p-4"
                data-testid={`invoice-card-${inv.number}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-zinc-400">{inv.number}</div>
                    <div className="text-sm text-white truncate mt-0.5">{inv.client_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{inv.client_email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm text-zinc-100">{fmtMoney(inv.total, inv.currency)}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Due {fmtDate(inv.due_date)}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                  <StatusPill s={inv.status} />
                  {inv.status === "pending_confirmation" && inv.payment_tx_hash && (
                    <span className="text-[10px] font-mono text-zinc-400">tx {shortHash(inv.payment_tx_hash)}</span>
                  )}
                </div>
                <div className="mt-2 -mx-2 border-t border-[#2c241e] pt-2">
                  <InvoiceRowActions inv={inv} actions={rowActions} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <AlertDialogContent
          data-testid="confirm-payment-dialog"
          className="bg-[#0d0d0d] border border-[#2c241e] text-zinc-100 sm:rounded-none p-0 max-w-md"
        >
          <div className="p-6">
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                <AlertTriangle size={12} strokeWidth={2} /> Manual confirmation
              </div>
              <AlertDialogTitle className="font-display text-2xl tracking-tight mt-2 text-white">
                Confirm payment received?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-zinc-400 leading-relaxed">
                Only confirm once you've actually seen the funds land in your wallet. This action
                cannot be undone — it triggers collaborator splits and writes a credit to your
                informational wallet ledger. Quill never holds your funds.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {confirmTarget && (
              <div className="mt-5 space-y-3 border border-[#2c241e] p-4 bg-black/40">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500 text-[10px] uppercase tracking-[0.18em]">Invoice</span>
                  <span className="font-mono text-zinc-200">{confirmTarget.number}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500 text-[10px] uppercase tracking-[0.18em]">Amount</span>
                  <span className="font-mono text-white">
                    {fmtMoney(confirmTarget.total, confirmTarget.currency)}
                  </span>
                </div>
                <div className="border-t border-[#2c241e] pt-3">
                  <div className="text-zinc-500 text-[10px] uppercase tracking-[0.18em] mb-1.5">
                    Transaction hash
                  </div>
                  {confirmTarget.payment_tx_hash ? (
                    <div className="flex items-center gap-2">
                      <span
                        className="font-mono text-xs text-zinc-200 break-all flex-1"
                        data-testid="dialog-tx-hash"
                      >
                        {confirmTarget.payment_tx_hash}
                      </span>
                      <button
                        onClick={() => copyTx(confirmTarget.payment_tx_hash)}
                        title="Copy"
                        data-testid="dialog-copy-tx"
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-[#1a1a1a] rounded transition-colors flex-shrink-0"
                      ><Copy size={12} strokeWidth={2} /></button>
                      {explorerFor(confirmTarget.payment_tx_hash) && (
                        <a
                          href={explorerFor(confirmTarget.payment_tx_hash).url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="dialog-explorer-link"
                          className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300 px-2 py-1 border border-emerald-500/30 hover:border-emerald-500/60 transition-colors flex-shrink-0"
                        >
                          {explorerFor(confirmTarget.payment_tx_hash).name}
                          <ExternalLink size={10} strokeWidth={2} />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-amber-400/80">
                      No hash was provided. Verify in your wallet manually before confirming.
                    </div>
                  )}
                </div>
              </div>
            )}

            <AlertDialogFooter className="mt-6 gap-2">
              <AlertDialogCancel
                data-testid="cancel-confirm-payment"
                className="bg-transparent border border-[#2c241e] text-zinc-300 hover:bg-[#161616] hover:text-white sm:rounded-none"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmPayment}
                disabled={confirming}
                data-testid="confirm-payment-action"
                className="bg-emerald-500 text-black hover:bg-emerald-400 sm:rounded-none disabled:opacity-50"
              >
                {confirming ? "Confirming…" : "Mark as paid"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {followup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50" data-testid="followup-modal">
          <div className="bg-[#1f1815] border border-[#2c241e] max-w-2xl w-full p-4 md:p-8 relative">
            <button
              onClick={() => setFollowup(null)}
              data-testid="close-followup-btn"
              className="absolute top-4 right-4 p-1 text-zinc-400 hover:text-white"
            ><X size={18} /></button>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 flex items-center gap-2">
              <Sparkles size={12} /> AI follow-up draft · {followup.number}
            </div>
            {followup.loading ? (
              <div className="mt-6 text-zinc-400 text-sm">Drafting…</div>
            ) : (
              <>
                <pre className="mt-6 whitespace-pre-wrap text-sm text-zinc-200 leading-relaxed font-sans">{followup.text}</pre>
                <button
                  onClick={() => { navigator.clipboard.writeText(followup.text); toast.success("Copied"); }}
                  data-testid="copy-draft-btn"
                  className="mt-6 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200 inline-flex items-center gap-2"
                ><Copy size={14} /> Copy draft</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
